export const PROJECT_MARGIN_CATEGORIES = [
  { code: "labor", label: "Mano de obra" },
  { code: "structures", label: "Estructuras" },
  { code: "inverters", label: "Inversores" },
  { code: "panels", label: "Placas" },
  { code: "cable", label: "Cableado" },
  { code: "material", label: "Material" },
  { code: "other", label: "Otros gastos" },
] as const;

export type ProjectMarginCategory = (typeof PROJECT_MARGIN_CATEGORIES)[number]["code"];

const normalize = (value: unknown) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("es")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

/** Deterministic category assignment; ambiguous/unrecognized items stay in Otros gastos. */
export function classifyProjectMarginCategory(input: {
  family?: unknown;
  productName?: unknown;
  description?: unknown;
}): ProjectMarginCategory {
  const family = normalize(input.family).replace(/ /g, "_");
  if (family === "estructuras") return "structures";
  if (family === "inversores") return "inverters";
  if (family === "paneles") return "panels";
  if (family === "cable_cc" || family === "cable_ca") return "cable";
  if (["smart_meter", "baterias", "cargadores_ve", "protecciones_cc", "protecciones_ca", "cuadros", "canalizacion", "conectores", "epis", "consumibles", "herramientas", "otros"].includes(family)) {
    return "material";
  }

  const text = normalize(`${String(input.productName ?? "")} ${String(input.description ?? "")}`);
  if (/\b(mano de obra|servicios? de instalacion|personal de instalacion)\b/.test(text)) return "labor";
  if (/\b(estructura|estructuras|soporte|anclaje)\b/.test(text)) return "structures";
  if (/\b(inversor|inversores)\b/.test(text)) return "inverters";
  if (/\b(placa|placas|panel|paneles|modulo fotovoltaico)\b/.test(text)) return "panels";
  if (/\b(cable|cableado)\b/.test(text)) return "cable";
  if (/\b(material|proteccion|cuadro electrico|bateria|conector|canalizacion)\b/.test(text)) return "material";
  return "other";
}

export type ProjectMarginCategoryTotals = {
  code: ProjectMarginCategory;
  label: string;
  salesBudgetCents: number;
  estimatedCostCents: number | null;
  registeredCostCents: number;
  manualAdjustmentCents: number;
  actualCostCents: number;
  benefitCents: number;
  marginPct: number | null;
  savingsVsEstimatedCents: number | null;
};

/** Reconciles rounded/detail costs to the canonical project total without losing cents. */
export function buildProjectMarginCategoryTotals(input: {
  budgetLines: Array<{ category: ProjectMarginCategory; saleCents: number; estimatedCostCents: number | null; actualCostCents: number }>;
  supplierCostCents: number;
  technicianCostCents: number;
  manualExpenseCostCents: number;
  unmatchedSupplierCosts?: Partial<Record<ProjectMarginCategory, number>>;
  adjustments: Partial<Record<ProjectMarginCategory, number>>;
}): ProjectMarginCategoryTotals[] {
  const totals = new Map<ProjectMarginCategory, ProjectMarginCategoryTotals>();
  for (const category of PROJECT_MARGIN_CATEGORIES) {
    totals.set(category.code, {
      code: category.code,
      label: category.label,
      salesBudgetCents: 0,
      estimatedCostCents: 0,
      registeredCostCents: 0,
      manualAdjustmentCents: input.adjustments[category.code] ?? 0,
      actualCostCents: 0,
      benefitCents: 0,
      marginPct: null,
      savingsVsEstimatedCents: null,
    });
  }

  for (const line of input.budgetLines) {
    const target = totals.get(line.category)!;
    target.salesBudgetCents += line.saleCents;
    if (target.estimatedCostCents !== null) {
      target.estimatedCostCents = line.estimatedCostCents === null
        ? null
        : target.estimatedCostCents + line.estimatedCostCents;
    }
  }

  // Matched invoice lines are assigned by the exact budget/product match. Unmatched
  // lines use catalog family/category; any rounding or missing-detail remainder is
  // reconciled to Otros gastos, keeping supplier costs equal to the canonical allocation.
  const rawSupplierCosts = new Map<ProjectMarginCategory, number>();
  for (const { code } of PROJECT_MARGIN_CATEGORIES) rawSupplierCosts.set(code, 0);
  for (const line of input.budgetLines) {
    rawSupplierCosts.set(line.category, (rawSupplierCosts.get(line.category) ?? 0) + line.actualCostCents);
  }
  for (const [code, value] of Object.entries(input.unmatchedSupplierCosts ?? {}) as Array<[ProjectMarginCategory, number]>) {
    rawSupplierCosts.set(code, (rawSupplierCosts.get(code) ?? 0) + value);
  }
  const rawSupplierTotal = Array.from(rawSupplierCosts.values()).reduce((sum, amount) => sum + amount, 0);
  const supplierTarget = Math.max(0, input.supplierCostCents);
  if (rawSupplierTotal > 0) {
    if (rawSupplierTotal <= supplierTarget) {
      for (const [code, amount] of rawSupplierCosts) totals.get(code)!.registeredCostCents += amount;
      totals.get("other")!.registeredCostCents += supplierTarget - rawSupplierTotal;
    } else {
      let distributed = 0;
      const byRemainder = Array.from(rawSupplierCosts.entries()).map(([code, amount]) => {
        const exact = amount * supplierTarget / rawSupplierTotal;
        const rounded = Math.floor(exact);
        distributed += rounded;
        return { code, rounded, fraction: exact - rounded };
      });
      byRemainder.sort((a, b) => b.fraction - a.fraction);
      for (let i = 0; i < supplierTarget - distributed; i += 1) byRemainder[i % byRemainder.length].rounded += 1;
      for (const item of byRemainder) totals.get(item.code)!.registeredCostCents += item.rounded;
    }
  } else {
    totals.get("other")!.registeredCostCents += supplierTarget;
  }

  // Technician expenses/dietas and finance manual expenses are canonical costs,
  // but have no reliable line-level product match; present them under Otros gastos.
  const other = totals.get("other")!;
  other.registeredCostCents += input.technicianCostCents + input.manualExpenseCostCents;

  return PROJECT_MARGIN_CATEGORIES.map(({ code }) => {
    const category = totals.get(code)!;
    const canonicalAdjustment = category.manualAdjustmentCents;
    category.actualCostCents = category.registeredCostCents + canonicalAdjustment;
    category.benefitCents = category.salesBudgetCents - category.actualCostCents;
    category.marginPct = category.salesBudgetCents > 0
      ? Number(((category.benefitCents / category.salesBudgetCents) * 100).toFixed(2))
      : null;
    category.savingsVsEstimatedCents = category.estimatedCostCents === null
      ? null
      : category.estimatedCostCents - category.actualCostCents;
    return category;
  });
}
